import React, { useEffect } from 'react';
import { 
  FiX, 
  FiClock, 
  FiUser, 
  FiCalendar, 
  FiVideo, 
  FiFileText, 
  FiDollarSign,
  FiImage,
  FiInfo,
  FiAward,
  FiStar,
  FiUsers,
  FiTrendingUp,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiPlayCircle,
  FiBookOpen,
  FiTag,
  FiHash,
  FiEye,
  FiDownload,
  FiShare2
} from "react-icons/fi";
import { 
  MdOutlineClose, 
  MdOutlineAccessTime,
  MdOutlinePerson,
  MdOutlineCalendarToday,
  MdOutlineVideoLibrary,
  MdOutlineDescription,
  MdOutlineAttachMoney,
  MdOutlineImage,
  MdOutlineInfo,
  MdOutlineStar,
  MdOutlinePeople,
  MdOutlinePlayCircle,
  MdOutlineBook,
  MdOutlineCategory,
  MdOutlineVisibility,
  MdOutlineDownload,
  MdOutlineShare
} from "react-icons/md";
import { 
  IoCloseOutline, 
  IoTimeOutline, 
  IoPersonOutline, 
  IoCalendarOutline, 
  IoVideocamOutline, 
  IoDocumentTextOutline, 
  IoTimerOutline, 
  IoWalletOutline, 
  IoInformationCircleOutline, 
  IoImageOutline,
  IoPlayCircleOutline,
  IoBookOutline,
  IoStarOutline,
  IoPeopleOutline
} from 'react-icons/io5';
import moment from 'moment';

const ViewModal = ({ onCancel, inputValue = {} }) => {
    // Destructure data with default values
    const {
        thumbUrl = '',
        videoUrl = '',
        title: contentTitle = '',
        isPaid = false,
        pricePerHour = 0,
        createdAt = new Date(),
        sloats = [],
        user = {},
        title
    } = inputValue;

    // Slot details calculation
    const slotDetails = sloats && sloats?.flatMap(slotGroup =>
        slotGroup.slots.map(slot => ({
            startTime: slot.startTime,
            endTime: slot.endTime,
            duration: moment(slot.endTime).diff(moment(slot.startTime), 'minutes'),
            status: slot.status || 'pending',
            bookBy: slot.bookBy ? 'Booked' : 'Available',
            zegoId: slot.zegoId || 'N/A'
        }))
    );

    // Total duration calculation
    const totalMinutes = slotDetails?.reduce((acc, slot) => acc + slot.duration, 0) || 0;
    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;

    // Status badge component
    const StatusBadge = ({ status }) => {
        const statusConfig = {
            'pending': { icon: FiAlertCircle, color: 'text-amber-700 bg-amber-50 border-amber-200', label: 'Pending' },
            'confirmed': { icon: FiCheckCircle, color: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: 'Confirmed' },
            'completed': { icon: FiCheckCircle, color: 'text-blue-700 bg-blue-50 border-blue-200', label: 'Completed' },
            'cancelled': { icon: FiXCircle, color: 'text-rose-700 bg-rose-50 border-rose-200', label: 'Cancelled' }
        };
        const config = statusConfig[status?.toLowerCase()] || { icon: FiAlertCircle, color: 'text-gray-700 bg-gray-50 border-gray-200', label: status || 'N/A' };
        const Icon = config.icon;
        
        return (
            <span className={`
                inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border
                ${config.color}
            `}>
                <Icon className="w-3.5 h-3.5" />
                {config.label}
            </span>
        );
    };

    // Booking status badge
    const BookingBadge = ({ bookBy }) => {
        if (bookBy === 'Booked') {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full">
                    <FiCheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                    Booked
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-full">
                <FiXCircle className="w-3.5 h-3.5 text-gray-400" />
                Available
            </span>
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl max-h-[95vh] overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-xl">
                            <MdOutlineVideoLibrary className="w-5 h-5 text-[#715EFE]" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-800">{title || 'Session Details'}</h2>
                            <p className="text-xs text-gray-400 hidden sm:block">Session information and details</p>
                        </div>
                    </div>
                    <button
                        onClick={onCancel}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto max-h-[80vh]">
                    <div className="grid gap-6 p-6 md:grid-cols-2">
                        {/* Media Section */}
                        <div className="space-y-6">
                            {thumbUrl && (
                                <div>
                                    <h3 className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-700">
                                        <FiImage className="w-4 h-4 text-[#715EFE]" />
                                        Content Preview
                                    </h3>
                                    <div className="rounded-xl overflow-hidden shadow-md border border-gray-200 bg-gray-100">
                                        <img
                                            src={thumbUrl}
                                            alt="Content Preview"
                                            className="w-full h-64 object-cover"
                                            onError={(e) => e.target.src = "/Img/library.png"}
                                        />
                                    </div>
                                </div>
                            )}

                            {videoUrl && (
                                <div>
                                    <h3 className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-700">
                                        <FiVideo className="w-4 h-4 text-[#715EFE]" />
                                        Video
                                    </h3>
                                    <div className="rounded-xl overflow-hidden shadow-md border border-gray-200 bg-black">
                                        <video
                                            controls
                                            className="w-full h-64 object-contain"
                                            src={videoUrl}
                                            onContextMenu={(e) => e.preventDefault()}
                                            disablePictureInPicture
                                            controlsList="nodownload noplaybackrate"
                                        >
                                            Your browser does not support the video tag.
                                        </video>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Details Section */}
                        <div className="space-y-6">
                            {/* Content Details */}
                            <div className="bg-gray-50/80 rounded-xl border border-gray-200 p-4 space-y-3">
                                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                                    <FiFileText className="w-4 h-4 text-[#715EFE]" />
                                    Content Details
                                </h3>
                                <div className="space-y-2">
                                    <div>
                                        <p className="text-xs font-medium text-gray-500">Title</p>
                                        <p className="text-sm font-semibold text-gray-800 p-2 bg-white rounded-lg border border-gray-100">
                                            {contentTitle || 'N/A'}
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                                                <FiCalendar className="w-3.5 h-3.5 text-gray-400" />
                                                Created On
                                            </p>
                                            <p className="text-sm font-medium text-gray-700 p-2 bg-white rounded-lg border border-gray-100">
                                                {moment(createdAt).format('DD-MM-YYYY')}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                                                <FiDollarSign className="w-3.5 h-3.5 text-gray-400" />
                                                Price
                                            </p>
                                            <p className="text-sm font-semibold text-emerald-600 p-2 bg-white rounded-lg border border-gray-100">
                                                {isPaid ? `$${pricePerHour}/hr` : 'Free'}
                                            </p>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                                            <FiClock className="w-3.5 h-3.5 text-gray-400" />
                                            Total Duration
                                        </p>
                                        <p className="text-sm font-medium text-gray-700 p-2 bg-white rounded-lg border border-gray-100">
                                            {totalMinutes ? `${totalHours}h ${remainingMinutes}m` : 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Expert Information */}
                            <div className="bg-gray-50/80 rounded-xl border border-gray-200 p-4 space-y-3">
                                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                                    <FiUser className="w-4 h-4 text-[#715EFE]" />
                                    Expert Information
                                </h3>
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#715EFE]/30 bg-gray-100 flex-shrink-0">
                                        <img
                                            src={user.profileImage || "/Img/user.png"}
                                            alt={user.fullName}
                                            className="w-full h-full object-cover"
                                            onError={(e) => e.target.src = "/Img/user.png"}
                                        />
                                    </div>
                                    <div>
                                        <h4 className="text-base font-semibold text-gray-800">{user.fullName || 'N/A'}</h4>
                                        <p className="text-xs text-gray-500">{user.profile || 'Expert'}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 p-3 bg-white rounded-lg border border-gray-100">
                                    <div className="text-center">
                                        <p className="text-lg font-bold text-[#715EFE]">${user.perHourCharge || 0}</p>
                                        <p className="text-[10px] text-gray-500">Hourly Rate</p>
                                    </div>
                                    <div className="text-center border-l border-r border-gray-100">
                                        <p className="text-lg font-bold text-[#715EFE]">{user.followers || 0}</p>
                                        <p className="text-[10px] text-gray-500">Followers</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-lg font-bold text-[#715EFE]">{user.rating ? user.rating.toFixed(2) : 0}</p>
                                        <p className="text-[10px] text-gray-500">Rating</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Slots Section */}
                    {slotDetails && slotDetails.length > 0 && (
                        <div className="border-t border-gray-100 p-6">
                            <h3 className="flex items-center gap-2 mb-4 text-sm font-semibold text-gray-700">
                                <FiCalendar className="w-4 h-4 text-[#715EFE]" />
                                Available Slots
                            </h3>
                            <div className="grid gap-4 md:grid-cols-2">
                                {slotDetails.map((slot, index) => (
                                    <div
                                        key={index}
                                        className="bg-gray-50/80 rounded-xl border border-gray-200 p-4 hover:bg-gray-50 transition-colors duration-200"
                                    >
                                        <div className="space-y-2.5">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <FiClock className="w-4 h-4 text-[#715EFE]" />
                                                    <span className="text-xs font-medium text-gray-500">Duration</span>
                                                </div>
                                                <span className="text-sm font-medium text-gray-700">
                                                    {slot.duration} minutes
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-sm">
                                                <div>
                                                    <p className="text-xs text-gray-400">Start</p>
                                                    <p className="font-medium text-gray-700">
                                                        {moment(slot.startTime).utc().format('MMM D, h:mm A')}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-400">End</p>
                                                    <p className="font-medium text-gray-700">
                                                        {moment(slot.endTime).utc().format('MMM D, h:mm A')}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                                <StatusBadge status={slot.status} />
                                                <BookingBadge bookBy={slot.bookBy} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewModal;